import { z } from "zod";

export const executionCommitMessageSchema = z
  .object({ commit_message: z.string().trim().min(1) })
  .strict();

export const executionCommitMessageSchemaJson = z.toJSONSchema(
  executionCommitMessageSchema,
);

export const executionStagingPlanSchema = z
  .object({
    files_to_stage: z.array(z.string().trim().min(1)),
    rationale: z.string().trim().min(1).nullable().optional(),
  })
  .strict();

export const executionGitPrepareSchema = executionStagingPlanSchema
  .extend({ commit_message: z.string().trim().min(1) })
  .strict();

export const executionGitPrepareSchemaJson = z.toJSONSchema(
  executionGitPrepareSchema,
);

export const executionVerificationCriterionSchema = z
  .object({
    category: z.enum(["functional", "quality"]),
    criterion_id: z.string().trim().min(1),
    description: z.string().trim().min(1),
    source: z.string().trim().min(1),
    verification_method: z.string().trim().min(1),
  })
  .strict();

export const executionVerificationRequirementCheckSchema = z
  .object({
    category: z.enum(["functional", "non_functional"]),
    criterion_id: z.string().trim().min(1).nullable().default(null),
    evidence: z.string(),
    requirement: z.string().trim().min(1),
    satisfied: z.boolean(),
    skip_reason: z.string().nullable().default(null),
    skip_requirement: z.boolean().default(false),
  })
  .strict();

export const executionPlanContractArtifactSchema = z
  .object({
    attachment_count: z.number().int().nonnegative(),
    dependencies: z.array(z.string().trim().min(1)),
    description: z.string().nullable(),
    implementation_details: z.string().nullable(),
    intent: z.string().nullable(),
    issue_key: z.string().trim().min(1).nullable(),
    reference_repository_slugs: z.array(z.string().trim().min(1)),
    repository_slug: z.string().trim().min(1),
    self_assessed_risk: z.enum(["low", "medium", "high"]).nullable(),
    technical_constraints: z.array(z.string().trim().min(1)),
    title: z.string().nullable(),
  })
  .strict();

export type ExecutionPlanContractArtifact = z.infer<
  typeof executionPlanContractArtifactSchema
>;

export const executionDispatchContractArtifactSchema = z
  .object({
    attachment_count: z.number().int().nonnegative(),
    base_revision: z.string().trim().min(1).nullable(),
    branch_name: z.string().trim().min(1),
    prior_node_summary_count: z.number().int().nonnegative(),
    repository_paths_count: z.number().int().nonnegative(),
    repository_slug: z.string().trim().min(1),
    selected_branch: z.string().trim().min(1).nullable(),
    tests_count: z.number().int().nonnegative(),
  })
  .strict();

export type ExecutionDispatchContractArtifact = z.infer<
  typeof executionDispatchContractArtifactSchema
>;

export const executionExploreArtifactSchema = z
  .object({
    architectural_context_summary: z.string().nullable(),
    context_block_line_count: z.number().int().nonnegative(),
    context_block_title: z.string().trim().min(1),
    file_reference_count: z.number().int().nonnegative(),
    key_pattern_count: z.number().int().nonnegative(),
    potential_risk_count: z.number().int().nonnegative(),
    runner: z.enum(["explore", "architect"]),
  })
  .strict();

export type ExecutionExploreArtifact = z.infer<
  typeof executionExploreArtifactSchema
>;

export const executionVerificationSeedArtifactSchema = z
  .object({
    criteria: z.array(executionVerificationCriterionSchema),
    override_step: z.number().int().positive().nullable(),
    sequence: z.number().int().positive(),
    source: z.enum(["plan", "agent_override"]),
  })
  .strict();

export type ExecutionVerificationSeedArtifact = z.infer<
  typeof executionVerificationSeedArtifactSchema
>;

export const executionVerificationResultArtifactSchema = z
  .object({
    functional_verified: z.boolean(),
    lint_status: z.enum(["clean", "warnings", "errors", "not_run"]),
    quality_verified: z.boolean(),
    ready_for_completion: z.boolean(),
    requirements_checked: z.array(executionVerificationRequirementCheckSchema),
    sequence: z.number().int().positive(),
    skip_reason: z.string().nullable(),
    skip_status_check: z.boolean(),
    tests_status: z.enum([
      "all_passing",
      "some_failing",
      "not_run",
      "no_tests",
    ]),
    type_check_status: z.enum(["passing", "errors", "not_run"]),
  })
  .strict();

export type ExecutionVerificationResultArtifact = z.infer<
  typeof executionVerificationResultArtifactSchema
>;

export const executionNodeContextArtifactsSchema = z
  .object({
    dispatch_contract: executionDispatchContractArtifactSchema.nullable(),
    explore_artifact: executionExploreArtifactSchema.nullable(),
    plan_contract: executionPlanContractArtifactSchema.nullable(),
    verification_results: z.array(executionVerificationResultArtifactSchema),
    verification_seeds: z.array(executionVerificationSeedArtifactSchema),
  })
  .strict();

export type ExecutionNodeContextArtifacts = z.infer<
  typeof executionNodeContextArtifactsSchema
>;

export const executionContextStageCompletedEventSchema = z
  .object({
    artifact: z.discriminatedUnion("stage", [
      z
        .object({
          data: executionExploreArtifactSchema,
          stage: z.literal("explore_artifact"),
        })
        .strict(),
      z
        .object({
          data: executionVerificationSeedArtifactSchema,
          stage: z.literal("verification_seed"),
        })
        .strict(),
      z
        .object({
          data: executionVerificationResultArtifactSchema,
          stage: z.literal("verification_result"),
        })
        .strict(),
    ]),
  })
  .strict();

export type ExecutionContextStageCompletedEvent = z.infer<
  typeof executionContextStageCompletedEventSchema
>;
